import {
  type PropsWithChildren,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeModeContext } from './ThemeModeContext';
import type { PaletteMode } from '@/ui/primitives';

const storageKey = 'flowqa-theme-mode';

const resolveStoredMode = (): PaletteMode => {
  if (typeof window === 'undefined') return 'dark';
  const stored = window.localStorage.getItem(storageKey);
  if (stored === 'light') return 'light';
  return 'dark';
};

export const ThemeModeProvider = ({ children }: PropsWithChildren): ReactElement => {
  const [mode, setModeState] = useState<PaletteMode>(() => resolveStoredMode());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleStorage = (event: StorageEvent): void => {
      if (event.key !== storageKey) return;
      const nextMode: PaletteMode = event.newValue === 'dark' ? 'dark' : 'light';
      setModeState(nextMode);
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const setMode = useCallback((nextMode: PaletteMode) => {
    setModeState(nextMode);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, nextMode);
    }
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prevMode) => {
      const nextMode: PaletteMode = prevMode === 'dark' ? 'light' : 'dark';
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, nextMode);
      }
      return nextMode;
    });
  }, []);

  const value = useMemo(() => ({ mode, setMode, toggleMode }), [mode, setMode, toggleMode]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};
